import { reaction } from 'mobx';
import type { IBatchStore } from './batch-store-types';
import logger from '../../utils/logger';
import { NETWORK_EVENT } from '../connection-store';
import { SwitchMode } from '../../components/DeviceAsDom/Switch';
import { checkNewVersionAvailable } from './check-new-version-available';
import { initRemoteBatchListener } from '../../containers/Batch/RemoteBatchController';
import { ProxyCommands } from '../../constants/ProxyCommands';
import { timerKey } from '../../utils/timer-manager/timer-manager';
import type{
  IMixer,
  ISwitch,
} from '../../../types/device/DeviceModels';

export const initListenersMixin = {

  checkProxyNeedsUpdate(this: IBatchStore) {
    const versionParts = this.proxyVersion?.split('.');
    const minor = Number(versionParts?.[1]);
    const patch = Number(versionParts?.[2]?.split('-')[0]);

    if (Number.isFinite(minor)) {
      this.proxyRequireUpdate = !!this.settingsStore.configuration?.proxyAddress
        && this.pacConnected
        && !!this.proxyVersion
        && (minor < 14 || (minor === 14 && patch < 4));
    }
  },

  initListenerAfterMaterialsLoaded(this: IBatchStore) {
    const { materialStore } = this.rootStore;

    if (!materialStore?.materialDetailsLoaded) {
      this.setTimeout(() => {
        this.initListenerAfterMaterialsLoaded();
      }, 1000);
      return;
    }

    this.injectCode(this.executeCode);

    initRemoteBatchListener((remoteData) => {
      this.sendRemoteInstruction(remoteData);
    });
  },

  initListeners(this: IBatchStore) {
    checkNewVersionAvailable(this);

    this.initListenerAfterMaterialsLoaded();
    this.checkMixerNoseplug();

    window.addEventListener(NETWORK_EVENT.CONNECTION_ERROR, () => {
      this.saveLog('warning: No internet connection!');
    });

    window.addEventListener(NETWORK_EVENT.CONNECTION_SLOW, () => {
      this.saveLog('warning: Slow internet connection');
    });

    window.onerror = (msg, source, lineNo, columnNo, error) => {
      this.saveLog(`error: ${error || msg}`);
      this.showError(`error: ${error || msg}`);
    };

    reaction(
      () => this.rootStore.connectionStore?.isOnline && !!this.rootStore.settingsStore?.configuration,
      (isOnline) => {
        logger.log('isOnline', isOnline);
        if (isOnline && this.rootStore.settingsStore?.configuration) {
          if (!this.running.length) {
            this.scheduleStore.syncOfflineOrders();
          }

          this.ticketStore.syncOfflineTicketsFromDispatch();
          this.ticketStore.syncOfflineTickets();

          if (this.materialStore.materialDetailsLoaded) {
            this.rootStore.newAuthStore?.sendOfflineRequests();
          }
        }
      },
    );

    reaction(
      () => Array.from(this.deviceStatus.values()),
      data => this.compareData(data),
    );

    reaction(
      () => this.isDemo,
      () => {
        this.clearAllTimers();
        if (!this.replayMode) {
          this.setInitialData();
        }
        this.queue = [];
        this.pacSimulator.reset();
        this.statusTime = null;
      },
    );

    reaction(
      () => this.isPacInitialCommandSent,
      () => {
        if (this.canEmit && !this.isDemo && !this.running.length) {
          this.holdScalesClosed();
        }
      },
    );

    reaction(
      () => this.statusTime,
      () => {
        this.checkPacConnection();
      },
    );

    reaction(
      () => this.inputs,
      () => {
        if (!this.canEmit || this.replayMode || this.maintenanceMode || !this.running.length) {
          return;
        }

        const { settingsStore, devicesStore } = this.rootStore;
        const { configuration }: any = settingsStore;
        const batchData = this.running[0];

        if (
          configuration.dischargeAuthorizationInput
          && !this.isInputPortOff(configuration.dischargeAuthorizationInput)
        ) {
          batchData.dischargeAuthorization = true;

          logger.log('discharge authorization checked', configuration.dischargeAuthorizationInput);

          if (batchData.allDrops > 1) {
            this.running.forEach(v => {
              if (v.uid === batchData.uid) {
                v.dischargeAuthorization = true;
              }
            });

            this.queue.forEach(v => {
              if (v.uid === batchData.uid) {
                v.dischargeAuthorization = true;
              }
            });
          }

        }

        devicesStore.switches
          .filter((s: ISwitch) => s.mode === SwitchMode.LatchOn)
          .forEach((s: ISwitch) => {
            if (s.statusPNo && !this.isInputPortOff(s.statusPNo)) {
              this.deviceStatus.set(s.id, {
                id: s.id,
                uid: batchData.uid,
                active: true,
              });
            }
          });

      },
    );
  },

  mixerNoseplugIsActive(this: IBatchStore, mixerId: number): boolean {
    const mixer = this.devicesStore.mixers.find((mixer: IMixer) => mixer.id === mixerId);
    if (!mixer) {
      return false;
    }

    const hasNoseplugInput = mixer.noseplugInputPNo > 0;
    const hasNoseplugOutput = mixer.noseplugOutputPNo > 0;
    const noseplugInputIsActive = hasNoseplugInput && !this.isInputPortOff(mixer.noseplugInputPNo);
    const noseplugOutputIsActive = hasNoseplugOutput && this.isOutputOn(mixer.noseplugOutputPNo);
    return noseplugInputIsActive || noseplugOutputIsActive;
  },

  switchButtonShouldBeDisabled(this: IBatchStore, switchDevice: ISwitch): boolean {
    const mixer = this.devicesStore.mixers
      .find((mixer: IMixer) => mixer.noseplugOutputPNo && mixer.noseplugOutputPNo === switchDevice.onPNo);

    return !!mixer && (!this.isInputPortOff(mixer.openTiltStatusPNo) || this.isInputPortOff(mixer.closeReturnStatusPNo));
  },

  checkMixerNoseplug(this: IBatchStore): void {
    const mixer = this.devicesStore.mixers.length ? this.devicesStore.mixers[0] : null;

    if (!mixer?.noseplugInputPNo && !mixer?.noseplugOutputPNo) {
      this.setTimeout(() => {
        this.checkMixerNoseplug();
      }, 5000);

      return;
    }

    logger.log('mixer init noseplug listener', mixer);

    reaction(
      () => this.mixerNoseplugIsActive(mixer.id),
      (isActive) => {
        logger.log('mixer noseplug is active:', isActive);

        this.timers.clear(timerKey.mixerNoseplug(mixer.id));

        if (isActive) {
          this.mixerDownButtonShouldBeDisabled = true;
        } else {
          this.timers.set(timerKey.mixerNoseplug(mixer.id), () => {
            this.mixerDownButtonShouldBeDisabled = false;
            logger.log('mixer down button should be active now after noseplug delay');
          }, (mixer.noseplugDelay || 0) * 1000);
        }
      },
      { fireImmediately: true },
    );
  },

  isPacUidValid(this: IBatchStore, uid: string): boolean {
    const { configuration }: any = this.rootStore.settingsStore;
    return configuration?.batchUID && configuration?.batchUID === uid;
  },

  getPacVersion(this: IBatchStore) {
    this.timers.clear(timerKey.version());

    if (!this.pacVersion) {
      this.pac.runWS(ProxyCommands.GET_VERSION);
      this.timers.set(timerKey.version(), () => {
        this.getPacVersion();
      }, 500, 'Get PAC version');
      return;
    }

    this.pac.runWS(ProxyCommands.GET_PROXY_VERSION);

    if (!this.isPacUidValid(this.pacVersion.uid)) {
      this.showError('PAC version incorrect!');
      return;
    }

    this.pacConnected = true;
  },

};
